import React, { useState, useEffect } from 'react';
import { getIntlLocale } from '../../../utils/functions';
import { useTranslation } from 'react-i18next';
import { PiClockBold, PiArrowClockwiseLight } from 'react-icons/pi';
import apis from '../../../utils/apis';
import Button from '../../shared/TailwindComponents/Button';
import Spinner from '../../shared/TailwindComponents/Spinner';

interface Version {
  VersionID: string;
  Modified: string;
  AccessTier: string;
  BlobType: string;
  Size: number;
}

interface VersionHistoryProps {
  fileId: string;
  folderId: string;
  fileName: string;
  onVersionRestore?: () => void;
  onClose: () => void;
}

/**
 * Version History Content Component
 * Displays file version history from Azure Blob Storage with restore functionality
 * Note: This component is rendered within GlobalModal and doesn't manage its own modal state
 */
const VersionHistory: React.FC<VersionHistoryProps> = ({
  fileId,
  folderId,
  fileName,
  onVersionRestore,
  onClose
}) => {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(null);
  const [confirmRestoreVersionId, setConfirmRestoreVersionId] = useState<string | null>(null);

  useEffect(() => {
    const fetchVersions = async () => {
      setLoading(true);
      try {
        const response = await apis({
          method: 'GET',
          url: `/files/file-versions?FileID=${fileId}&FolderID=${folderId}`
        });

        setVersions(response.data.Versions || []);
      } catch (error) {
        console.error('Error fetching versions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVersions();
  }, [fileId, folderId]);

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(2)} ${units[unitIndex]}`;
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString(getIntlLocale(), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const confirmRestore = async () => {
    if (!confirmRestoreVersionId) return;

    setRestoringVersionId(confirmRestoreVersionId);
    try {
      await apis({
        method: 'POST',
        url: '/files/restore-file-version',
        data: {
          FileID: fileId,
          VersionID: confirmRestoreVersionId,
          FolderID: folderId
        }
      });

      onVersionRestore?.();
      onClose();
    } catch (error) {
      console.error('Error restoring version:', error);
      alert('Failed to restore version. Check console for details.');
    } finally {
      setRestoringVersionId(null);
      setConfirmRestoreVersionId(null);
    }
  };

  const handleRestoreVersion = (versionId: string) => {
    setConfirmRestoreVersionId(versionId);
  };

  return (
    <div className="w-full">
      {/* Content */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center">
            <Spinner size="sm" className="!h-8 !w-8 inline-block" />
            <p className="mt-3 text-sm text-fg-muted">{t('Loading versions...')}</p>
          </div>
        ) : versions.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-sm text-fg-muted">{t('No versions found')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-hairline bg-surface-sunken/70">
                  <th className="px-4 py-2 text-start text-xs font-semibold text-fg-strong">
                    {t('Modified')}
                  </th>
                  <th className="px-4 py-2 text-start text-xs font-semibold text-fg-strong">
                    {t('Size')}
                  </th>
                  <th className="px-4 py-2 text-start text-xs font-semibold text-fg-strong">
                    {t('Actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {versions.map((version) => (
                  <tr
                    key={version.VersionID}
                    className="border-b border-hairline-soft hover:bg-surface-sunken/70 transition-colors"
                  >
                    <td className="px-4 py-3 text-xs text-fg-body">
                      {formatDate(version.Modified)}
                    </td>
                    <td className="px-4 py-3 text-xs text-fg-body">
                      {formatFileSize(version.Size)}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <button
                        onClick={() => handleRestoreVersion(version.VersionID)}
                        disabled={restoringVersionId === version.VersionID}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs border border-hairline bg-surface-sunken/70 text-fg-body hover:border-champagne/60 hover:text-champagne disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <PiArrowClockwiseLight className="w-3.5 h-3.5" />
                        {restoringVersionId === version.VersionID ? t('Restoring...') : t('Restore')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmRestoreVersionId && (
        <div className="mt-6 pt-6 border-t border-hairline">
          <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-900/50 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-semibold text-warning-900 dark:text-warning-200 mb-2">
              {t('Confirm Version Restore')}
            </h4>
            <p className="text-xs text-warning-800 dark:text-warning-300">
              {t('Are you sure you want to restore this version? This will replace the current file content.')}
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => setConfirmRestoreVersionId(null)}
              variant="neutral"
              size="small"
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={confirmRestore}
              variant="primary"
              size="small"
              disabled={restoringVersionId === confirmRestoreVersionId}
            >
              {restoringVersionId === confirmRestoreVersionId ? t('Restoring...') : t('Confirm')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionHistory;
